<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import type { FormServerErrorResponse, IResponseData } from '~/types'
import { useApiPost } from '~/composables/useApiPost'
import type { IDiscussionCategoryData } from '~/components/widget/discussions'

const { t: _t } = useI18n()
const config = useRuntimeConfig()
const { trackEvent } = useAnalytics()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredQuestionCategory: useValidationMessage('requiredQuestionCategory'),
  requiredField: useValidationMessage('requiredField'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue50: useValidationMessage('maxValue', 50),
  requiredFieldMessage: useValidationMessage('requiredFieldMessage'),
  minValue3: useValidationMessage('minValue', 3),
  maxValue500: useValidationMessage('maxValue', 500),
  agreement: useValidationMessage('agreement'),

}

const apiParams = {
  per_page: 100
}

const { data } = useApi<IResponseData<IDiscussionCategoryData[]>>(
  () => useUrlWithParams('/question_categories', apiParams),
  {
    key: 'question_categories'
  }
)
const questionCategories = computed(() => data.value?.data)

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      question_category_id: z
        .number()
        .min(0, { message: validationMessages.requiredQuestionCategory }),
      title: z
        .string({ message: validationMessages.requiredField })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      description: z
        .string({ message: validationMessages.requiredFieldMessage })
        .min(3, { message: validationMessages.minValue3 })
        .max(500, { message: validationMessages.maxValue500 }),
      agreement: z
        .boolean({
          required_error: validationMessages.agreement
        })
        .refine((val) => val, {
          message: validationMessages.agreement
        })
    })
  )
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    question_category_id: questionCategories.value?.[0]?.id ?? undefined,
    agreement: false
  }
})

watch(questionCategories, (newQuestionCategories) => {
  if (newQuestionCategories?.length) {
    form.setFieldValue('question_category_id', newQuestionCategories[0].id)
  }
})

const alert = ref(false)
const requestId = ref('')

const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    }
  }

}

const onSubmit = form.handleSubmit(async (values) => {
  const requestBody: Record<string, unknown> = {
    question_category_id: values.question_category_id,
    title: values.title,
    description: values.description
  }

  const { data, error } = await useApiPost<IResponseData<{ id: string }>>('/questions', requestBody)
  if (error) {
    handleServerValidationErrors(error, form.setFieldError, [
      'question_category_id',
      'title',
      'description'
    ])
  } else {
    trackEvent('contact_form_submit', { form_type: 'question' })
    requestId.value = data?.data.id ?? ''
    alert.value = true
  }
})
</script>

<template>
  <div>
    <Alert v-if="alert" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertTitle>{{ $t('feature.ask_question_form.success.title') }}</AlertTitle>
      <AlertDescription>
        <div>
          {{ $t('feature.ask_question_form.success.description') }}
        </div>
        <div>{{ $t('feature.ask_question_form.success.request_id') }}{{ requestId }}.</div>
      </AlertDescription>
    </Alert>
    <form
      v-else
      v-webmcp-tool="{
        name: 'ask_bank_specialists',
        description:
          'Ask bank specialists of Kyrgyzstan a question about deposits, credits, cards or transfers. The question is published on the site with the answer.'
      }"
      class="flex flex-col gap-5"
      @submit="onSubmit"
    >
      <div class="grid grid-cols-1 gap-4">
        <ClientOnly>
          <FormField v-slot="{ componentField, errors }" name="question_category_id">
            <FormItem>
              <FormLabel>{{ $t('feature.ask_question_form.labels.category') }}</FormLabel>
              <Select v-bind="componentField">
                <SelectTrigger
                  :aria-label="$t('feature.ask_question_form.labels.category')"
                  :aria-invalid="errors.length > 0"
                >
                  <SelectValue
                    :placeholder="$t('feature.ask_question_form.placeholders.select_category')"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="category in questionCategories"
                    :key="category.id"
                    :value="category.id"
                    >{{ category.title }}</SelectItem
                  >
                </SelectContent>
              </Select>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
        </ClientOnly>

        <FormField v-slot="{ componentField, errors }" name="title">
          <FormItem>
            <FormLabel>{{ $t('feature.ask_question_form.labels.title') }}</FormLabel>
            <FormControl>
              <Input type="text" :placeholder="$t('common.title_column')" v-bind="componentField" />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField, errors }" name="description">
          <FormItem>
            <FormLabel>{{ $t('feature.ask_question_form.labels.question') }}</FormLabel>
            <FormControl>
              <Textarea
                :placeholder="$t('feature.ask_question_form.placeholders.question')"
                v-bind="componentField"
              />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
      </div>

      <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
        <FormItem>
          <FormControl>
            <div class="flex items-center space-x-2">
              <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
              <Label for="terms">
                {{ $t('feature.ask_question_form.agreement') }}
                <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                  {{ $t('feature.ask_question_form.agreement_link') }}
                </NuxtLinkLocale>
              </Label>
            </div>
          </FormControl>
          <FormMessage v-if="errorMessage" :message="errorMessage" />
        </FormItem>
      </FormField>

      <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
        <Button type="submit" class="mt-4 md:mt-0">{{
          $t('feature.ask_question_form.submit')
        }}</Button>
      </div>
    </form>
  </div>
</template>
